import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Layout, { DateInput, TablePagination, usePagination } from "./Layout";
import { formatDate, getApiUrl, getTodayIso } from "./config";

function PaymentsReport() {
  const today = getTodayIso();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [direction, setDirection] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [rows, setRows] = useState([]);

  const getModeLabel = (row) => {
    const cash = Number(row?.cash_amount) || 0;
    const upi = Number(row?.upi_amount) || 0;
    const bank = Number(row?.bank_amount) || 0;
    const flags = [cash > 0, upi > 0, bank > 0].filter(Boolean).length;
    if (flags === 0) return "-";
    if (flags === 1) {
      if (cash > 0) return "Cash";
      if (upi > 0) return "UPI";
      return "Bank Transfer";
    }
    return "Mixed";
  };

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiUrl()}/accounts`);
      setAccounts(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      const params = { from, to };
      if (direction) params.direction = direction;
      if (accountId) params.account_id = accountId;
      const res = await axios.get(`${getApiUrl()}/reports/payments`, { params });
      setRows(res.data || []);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to load payments";
      alert(message);
    }
  }, [from, to, direction, accountId]);

  const downloadTally = async (params, fallbackFilename) => {
    const res = await axios.get(`${getApiUrl()}/exports/tally`, {
      params,
      responseType: "blob",
    });
    const disposition = res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"]);
    const match = String(disposition || "").match(/filename="?([^"]+)"?/i);
    const filename = match ? match[1] : fallbackFilename;
    const blobUrl = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleExportPaymentsTally = async () => {
    try {
      const params = { type: "payments", from, to };
      if (direction) params.direction = direction;
      if (accountId) params.account_id = accountId;
      await downloadTally(params, "tally_payments.xml");
    } catch (err) {
      console.error(err);
      alert("Failed to export Tally XML");
    }
  };

  const handleExportAllTally = async () => {
    try {
      const params = { type: "all", from, to };
      await downloadTally(params, "tally_all.xml");
    } catch (err) {
      console.error(err);
      alert("Failed to export Tally XML");
    }
  };

  const handleExportPdf = () => {
    window.print();
  };

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    pagedItems,
    totalItems,
    startIndex,
    endIndex
  } = usePagination(rows, 10);

  return (
    <Layout>
      <div className="container mt-4">
        <h2>Payments</h2>

        <div className="card p-4 mb-4 no-print">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-bold">From</label>
              <DateInput value={from} onChange={setFrom} />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">To</label>
              <DateInput value={to} onChange={setTo} />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Direction</label>
              <select className="form-select" value={direction} onChange={(e) => setDirection(e.target.value)}>
                <option value="">All</option>
                <option value="in">In</option>
                <option value="out">Out</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Account</label>
              <select className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">All</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.type})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <button type="button" className="btn btn-primary" onClick={handleExportPaymentsTally}>
              Export Payments (Tally XML)
            </button>
            <button type="button" className="btn btn-outline-primary" onClick={handleExportAllTally}>
              Export All (Tally XML)
            </button>
            <button type="button" className="btn btn-outline-dark" onClick={handleExportPdf}>
              Export PDF
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Date</th>
                <th>Direction</th>
                <th>Account</th>
                <th>Type</th>
                <th>Cash</th>
                <th>UPI</th>
                <th>Bank</th>
                <th>Mode</th>
                <th>Total</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((r) => (
                <tr key={r.id}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.direction}</td>
                  <td>{r.account_name}</td>
                  <td>{r.account_type}</td>
                  <td>₹ {(parseFloat(r.cash_amount) || 0).toFixed(2)}</td>
                  <td>₹ {(parseFloat(r.upi_amount) || 0).toFixed(2)}</td>
                  <td>₹ {(parseFloat(r.bank_amount) || 0).toFixed(2)}</td>
                  <td>{getModeLabel(r)}</td>
                  <td className="fw-bold">₹ {(parseFloat(r.total_amount) || 0).toFixed(2)}</td>
                  <td>
                    {r.source_type} #{r.source_id}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center">
                    No payments
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="4" className="text-end">
                  Total
                </th>
                <th>₹ {rows.reduce((s, r) => s + (parseFloat(r.cash_amount) || 0), 0).toFixed(2)}</th>
                <th>₹ {rows.reduce((s, r) => s + (parseFloat(r.upi_amount) || 0), 0).toFixed(2)}</th>
                <th>₹ {rows.reduce((s, r) => s + (parseFloat(r.bank_amount) || 0), 0).toFixed(2)}</th>
                <th>₹ {rows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0).toFixed(2)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setCurrentPage}
        />
      </div>
    </Layout>
  );
}

export default PaymentsReport;
